import update from 'immutability-helper';
import { FC, Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { Button, Card, CardBody, Col, ListGroup, Row } from 'reactstrap';
import {
  AuthUser,
  Campaign,
  CampaignQuestion,
  QuestionDefaults,
  QuestionEditorAvailableSectionsDescriptor,
  ReduxState,
  ShouldShowAIAssistedQuestionDescriptorType,
} from 'types';
import { isPhaseLocked } from 'utils/models/Campaign';
import { getUserIsSuperAdmin } from 'utils/models/User';
import { useCampaignEditorPhase } from 'views/Administration/CampaignEditorPhaseContext';
import { CUSTOM_QUESTION_NONANONYMOUS_PREFIX } from '../../../utils/models/Performance';
import ModalFeedbackRequestSelector from '../../Feedback/ModalFeedbackRequestSelector';
import QuestionEditor, { generateUniqueQuestionId } from './QuestionEditor';
import QuestionInput from './QuestionInput';
import { INPUT_TYPES } from './ValidatedInputTypes';

type QuestionsEditorProps = {
  addText?: string;
  campaign?: Campaign | null;
  canBeAnonymous?: boolean | null;
  canBePrivate?: boolean | null;
  canAllowCalibration?: boolean | null;
  canHaveAiAssistedQuestions?: boolean | null;
  excludeSpecialQuestions?: string[] | null;
  allowedQuestionTypes?: string[] | null;
  disableTypeChange?: boolean;
  typeChangeDisabledMessage?: string;
  inModal?: boolean | null;
  isReadOnly?: boolean | null;
  jsonPath?: string | null;
  // name?: string; this was listed in PropTypes but never passed nor used.
  onChange: (val: object[]) => void;
  preserveExistingAnswers?: boolean;
  // Questiond default for specific question types (optional)
  questionDefaults?: QuestionDefaults;
  disableReorder?: boolean;
  deleteLabels?: {
    action: string;
    title: string;
    description: string;
  };
  submitButtonType?: string | null;
  disableUnsavedChangesPrompt?: boolean;
  // no route-leave prompt from inside the question modal (an outer form owns it)
  translationNamespace?: string | null;
  value?: CampaignQuestion[];
  includeExcludeLabel?: string;
  displayFactor?: boolean;
  aiAssistedQuestionsVisibilityField?: string | null;
  shouldShowAIAssistedQuestionDescriptor?: ShouldShowAIAssistedQuestionDescriptorType | null;
  availableSectionsDescriptor?: QuestionEditorAvailableSectionsDescriptor | null;
};

const QuestionsEditor: FC<QuestionsEditorProps> = ({
  addText,
  campaign,
  canBeAnonymous,
  canBePrivate,
  canAllowCalibration,
  canHaveAiAssistedQuestions,
  excludeSpecialQuestions,
  inModal,
  isReadOnly = false,
  jsonPath,
  onChange,
  preserveExistingAnswers,
  questionDefaults,
  disableReorder,
  deleteLabels,
  submitButtonType,
  disableUnsavedChangesPrompt,
  translationNamespace,
  value,
  allowedQuestionTypes,
  disableTypeChange,
  typeChangeDisabledMessage,
  includeExcludeLabel,
  displayFactor,
  aiAssistedQuestionsVisibilityField,
  shouldShowAIAssistedQuestionDescriptor,
  availableSectionsDescriptor,
}) => {
  const { formatMessage } = useIntl();

  const campaignEditorPhaseContext = useCampaignEditorPhase();
  const authUser = useSelector<ReduxState, AuthUser | undefined>(
    (state) => state?.authUser
  );
  const isSuperAdmin = getUserIsSuperAdmin(authUser);

  const propsOnChange = onChange;

  const [addQuestionObject, setAddQuestionObject] = useState(undefined);
  const [showAddQuestionModal, setShowAddQuestionModal] = useState(false);
  const toggleAddQuestionModal = useCallback(() => {
    setShowAddQuestionModal(!showAddQuestionModal);
  }, [showAddQuestionModal]);
  const [showImportQuestionsModal, setShowImportQuestionsModal] =
    useState(false);
  const toggleImportQuestionsModal = useCallback(() => {
    setShowImportQuestionsModal(!showImportQuestionsModal);
  }, [showImportQuestionsModal]);

  const readOnlyBecauseAnsweredAlready = isPhaseLocked(
    campaign?.campaign_question_locks ?? {},
    campaignEditorPhaseContext?.phaseType ?? ''
  );

  // if no campaign is provided, hide some of the richer/more advanced features
  // (e.g. in the case of feedback requests)
  const hideAdvancedFeatures = useMemo(() => {
    return campaign ? false : true;
  }, [campaign]);

  useEffect(() => {
    // clear out the add question object if this is a close
    if (!showAddQuestionModal) {
      setAddQuestionObject(undefined);
    }
  }, [showAddQuestionModal]);

  const onAddQuestion = useCallback(
    (newQuestion) => {
      const newListValue = [...(value ?? []), newQuestion];
      propsOnChange(newListValue);
    },
    [propsOnChange, value]
  );

  const onEditQuestion = useCallback(
    (questionIndex, newQuestionValue) => {
      const newListValue =
        value?.map((item, index) => {
          if (index === questionIndex) {
            return newQuestionValue;
          }
          return item;
        }) ?? [];
      propsOnChange(newListValue);
    },
    [propsOnChange, value]
  );

  const onCopyQuestion = useCallback((question) => {
    // duplicate question and generate different unique ID
    setAddQuestionObject({
      ...question,
      name: generateUniqueQuestionId(),
    });
    setShowAddQuestionModal(true);
  }, []);

  const onDeleteQuestion = useCallback(
    (question) => {
      const newListValue = value?.filter((r) => r !== question) ?? [];
      propsOnChange(newListValue);
    },
    [propsOnChange, value]
  );

  const questions = useMemo(() => {
    return value;
  }, [value]);

  const moveQuestion = useCallback(
    (dragIndex, hoverIndex) => {
      propsOnChange(
        // @ts-expect-error
        update(questions, {
          $splice: [
            [dragIndex, 1],
            // @ts-expect-error
            [hoverIndex, 0, questions[dragIndex]],
          ],
        })
      );
    },
    [propsOnChange, questions]
  );

  const questionsList = useMemo(() => {
    if (!questions || questions.length === 0) return null;

    const renderQuestion = (question: CampaignQuestion, index: number) => (
      <QuestionInput
        key={index}
        hoverIndex={index}
        campaign={campaign}
        value={question}
        callback={(q) => onEditQuestion(index, q)}
        onCopy={() => onCopyQuestion(question)}
        onDelete={() => onDeleteQuestion(question)}
        deleteLabels={deleteLabels}
        moveQuestion={disableReorder ? undefined : moveQuestion}
        disabled={!!isReadOnly /*Force boolean for ts.*/}
        canBePrivate={canBePrivate}
        canAllowCalibration={canAllowCalibration}
        canBeAnonymous={canBeAnonymous}
        canHaveAiAssistedQuestions={canHaveAiAssistedQuestions}
        aiAssistedQuestionsVisibilityField={aiAssistedQuestionsVisibilityField}
        shouldShowAIAssistedQuestionDescriptor={
          shouldShowAIAssistedQuestionDescriptor
        }
        preserveExistingAnswers={preserveExistingAnswers}
        submitButtonType={submitButtonType}
        disableUnsavedChangesPrompt={disableUnsavedChangesPrompt}
        hideAdvancedFeatures={hideAdvancedFeatures}
        inModal={inModal}
        excludeSpecialQuestions={excludeSpecialQuestions}
        allowedQuestionTypes={allowedQuestionTypes}
        disableTypeChange={disableTypeChange}
        typeChangeDisabledMessage={typeChangeDisabledMessage}
        includeExcludeLabel={includeExcludeLabel}
        displayFactor={displayFactor}
        availableSectionsDescriptor={availableSectionsDescriptor}
        // only relevant for in modal situation
        autoFocus={true}
        translationNamespace={translationNamespace}
        jsonPath={
          question.name == null || jsonPath == null
            ? undefined
            : `${jsonPath}[?(@.name=='${question.name}')]`
        }
      />
    );

    if (
      !availableSectionsDescriptor ||
      availableSectionsDescriptor.sections.length === 0
    ) {
      return <>{questions.map(renderQuestion)}</>;
    }

    const sections = availableSectionsDescriptor.sections;
    const firstSectionId = sections[0].id;
    const indexedQuestions = questions.map((question, index) => ({
      question,
      index,
    }));
    const groups = sections
      .map((section) => ({
        section,
        items: indexedQuestions.filter(
          ({ question }) => (question.section ?? firstSectionId) === section.id
        ),
      }))
      .filter(({ items }) => items.length > 0);

    return (
      <>
        {groups.map(({ section, items }, groupIndex) => (
          <Fragment key={section.id}>
            {groupIndex > 0 && (
              <div className="mt-5 mb-4">
                <h4 className="mb-2 fw-bold">{section.name}</h4>
                {section.helperText && (
                  <small className="form-text text-muted d-block mb-3">
                    {section.helperText}
                  </small>
                )}
                <hr className="mt-0" />
              </div>
            )}
            {items.map(({ question, index }) =>
              renderQuestion(question, index)
            )}
          </Fragment>
        ))}
      </>
    );
  }, [
    aiAssistedQuestionsVisibilityField,
    allowedQuestionTypes,
    disableTypeChange,
    typeChangeDisabledMessage,
    campaign,
    canBeAnonymous,
    canBePrivate,
    canAllowCalibration,
    canHaveAiAssistedQuestions,
    deleteLabels,
    disableReorder,
    excludeSpecialQuestions,
    hideAdvancedFeatures,
    includeExcludeLabel,
    inModal,
    isReadOnly,
    jsonPath,
    moveQuestion,
    onCopyQuestion,
    onDeleteQuestion,
    onEditQuestion,
    preserveExistingAnswers,
    questions,
    shouldShowAIAssistedQuestionDescriptor,
    submitButtonType,
    disableUnsavedChangesPrompt,
    translationNamespace,
    displayFactor,
    availableSectionsDescriptor,
  ]);

  const onAddQuestionClicked = useCallback(() => {
    // if we're in a modal, we don't want to show the modal for adding a question,
    // we just want to create it right away
    if (inModal) {
      onAddQuestion({
        // note: must start with CUSTOM_QUESTION_NONANONYMOUS_PREFIX
        // for it to be save properly in the perf flow
        name: CUSTOM_QUESTION_NONANONYMOUS_PREFIX + generateUniqueQuestionId(),
        label: '',
        type: INPUT_TYPES.TEXTAREA,
      });
    } else {
      toggleAddQuestionModal();
    }
  }, [onAddQuestion, inModal, toggleAddQuestionModal]);

  const createQuestionButton = useMemo(() => {
    return (
      <Button
        color="white"
        onClick={onAddQuestionClicked}
        disabled={
          isReadOnly || (readOnlyBecauseAnsweredAlready && !isSuperAdmin)
        }
      >
        <i
          className="fe fe-plus"
          style={{ position: 'relative', top: '2px' }}
        />{' '}
        {addText ??
          formatMessage({
            id: 'app.views.widgets.inputs.questions_editor.create_question',
            defaultMessage: 'Create question',
          })}
      </Button>
    );
  }, [
    addText,
    formatMessage,
    isReadOnly,
    isSuperAdmin,
    onAddQuestionClicked,
    readOnlyBecauseAnsweredAlready,
  ]);

  const onImportQuestionsFromFeedbackRequest = useCallback(
    (fr) => {
      // add the custom questions from this feedback request to the current set of questions
      // @ts-expect-error
      const newListValue = [...value, ...fr.custom_questions];
      propsOnChange(newListValue);
    },
    [propsOnChange, value]
  );

  const existingQuestionNames = useMemo(
    () => (value ? value.map((x) => x.name) : []),
    [value]
  );

  const output = useMemo(
    () => (
      <>
        <QuestionEditor
          campaign={campaign}
          isOpen={showAddQuestionModal}
          toggle={toggleAddQuestionModal}
          question={addQuestionObject}
          extraProperties={{
            canBePrivate: canBePrivate,
            canAllowCalibration: canAllowCalibration,
            canBeAnonymous: canBeAnonymous,
            canHaveAiAssistedQuestions: canHaveAiAssistedQuestions,
            aiAssistedQuestionsVisibilityField:
              aiAssistedQuestionsVisibilityField,
            shouldShowAIAssistedQuestionDescriptor:
              shouldShowAIAssistedQuestionDescriptor,
          }}
          callback={onAddQuestion}
          title={formatMessage({
            id: 'app.views.widgets.inputs.questions_editor.title.create_question',
            defaultMessage: 'Create question',
          })}
          submitText={formatMessage({
            id: 'app.views.widgets.inputs.questions_editor.submit_text.create_question',
            defaultMessage: 'Create question',
          })}
          existingSpecialQuestions={existingQuestionNames}
          excludeSpecialQuestions={excludeSpecialQuestions}
          questionDefaults={questionDefaults}
          submitButtonType={submitButtonType}
          disableUnsavedChangesPrompt={disableUnsavedChangesPrompt}
          hideAdvancedFeatures={hideAdvancedFeatures}
          translationNamespace={translationNamespace}
          allowedQuestionTypes={allowedQuestionTypes}
          includeExcludeLabel={includeExcludeLabel}
          displayFactor={displayFactor}
          canHaveAiAssistedQuestions={canHaveAiAssistedQuestions}
          aiAssistedQuestionsVisibilityField={
            aiAssistedQuestionsVisibilityField
          }
          availableSectionsDescriptor={availableSectionsDescriptor}
        />
        {
          // @ts-expect-error
          questions?.length > 0 && (
            <>
              {inModal && (
                <Card>
                  <CardBody className="my-n3">
                    <ListGroup className="list-group-flush my-n3">
                      {questionsList}
                    </ListGroup>
                  </CardBody>
                </Card>
              )}
              {!inModal && questionsList}
            </>
          )
        }
        {inModal && (
          <Row className="align-items-center">
            <Col className="col-auto">{createQuestionButton}</Col>
            <Col className="ps-0">
              <ModalFeedbackRequestSelector
                isOpen={showImportQuestionsModal}
                toggle={toggleImportQuestionsModal}
                callback={onImportQuestionsFromFeedbackRequest}
              />
              <Button
                className="p-0"
                color="link"
                onClick={toggleImportQuestionsModal}
                disabled={isReadOnly}
              >
                <FormattedMessage
                  id="app.views.widgets.inputs.questions_editor.or_import"
                  defaultMessage="or import from a template or previous request..."
                />
              </Button>
            </Col>
          </Row>
        )}
        {!inModal && createQuestionButton}
      </>
    ),
    [
      addQuestionObject,
      aiAssistedQuestionsVisibilityField,
      allowedQuestionTypes,
      campaign,
      canBeAnonymous,
      canBePrivate,
      canAllowCalibration,
      canHaveAiAssistedQuestions,
      createQuestionButton,
      excludeSpecialQuestions,
      existingQuestionNames,
      formatMessage,
      hideAdvancedFeatures,
      includeExcludeLabel,
      inModal,
      isReadOnly,
      onAddQuestion,
      onImportQuestionsFromFeedbackRequest,
      questionDefaults,
      questions?.length,
      questionsList,
      shouldShowAIAssistedQuestionDescriptor,
      showAddQuestionModal,
      showImportQuestionsModal,
      submitButtonType,
      disableUnsavedChangesPrompt,
      toggleAddQuestionModal,
      toggleImportQuestionsModal,
      availableSectionsDescriptor,
      translationNamespace,
      displayFactor,
    ]
  );

  return output;
};

export default QuestionsEditor;
